/**
 * Wall-clock plausibility for device-reported positions and frozen-at-duration advance.
 * Shared by PositionReconcile (stall path), renderer-position-changed, and UPnP poller.
 */

import { isAdvanceGuardDurationTrustworthy } from '../utils/trackDuration';

export const POSITION_PLAUSIBILITY_EPSILON_SECS = 3;
export const ADVANCE_WALL_CLOCK_GRACE_SECS = 5;
/** Genuine track-end window for Pattern 1 frozen-at-end yield (position evidence). */
export const GENUINE_TRACK_END_TOLERANCE_BEFORE_SECS = 2;
export const GENUINE_TRACK_END_TOLERANCE_AFTER_SECS = 5;

let playUriAtMs = 0;
let effectiveStartOffsetSecs = 0;
/** Track id for the current selection/playUri epoch — stale end events must match. */
let advanceEpochTrackId: string | null = null;
/** Suppressions at genuine track end while wall-clock guard still rejects — max one before force. */
let frozenEndWallClockSuppressionCount = 0;
/** Connector #453: exclusive open failed — never wall-clock-advance on timer/stale position. */
let suppressWallClockAdvanceActive = false;

export function notePlayUriDispatched(startSeconds?: number | null): void {
  playUriAtMs = Date.now();
  effectiveStartOffsetSecs = startSeconds != null && startSeconds > 0
    ? Math.floor(startSeconds)
    : 0;
}

/** Reset wall-clock baseline when a new queue track is selected (before playUri). */
export function noteTrackSelectionStarted(trackId: string | null | undefined): void {
  advanceEpochTrackId = trackId != null ? String(trackId) : null;
  playUriAtMs = Date.now();
  effectiveStartOffsetSecs = 0;
  frozenEndWallClockSuppressionCount = 0;
}

export function getAdvanceEpochTrackId(): string | null {
  return advanceEpochTrackId;
}

export function noteSeekDispatched(targetSecs: number): void {
  effectiveStartOffsetSecs = Math.max(0, Math.floor(targetSecs));
}

export function getEffectiveStartOffsetSecs(): number {
  return effectiveStartOffsetSecs;
}

export function getSincePlayUriMs(): number {
  return playUriAtMs > 0 ? Date.now() - playUriAtMs : 0;
}

/** Active playUri epoch — wall-clock end advance requires a live session. */
export function hasActivePlayUriSession(): boolean {
  return playUriAtMs > 0 && advanceEpochTrackId != null;
}

export function resetPositionReconcileContext(): void {
  playUriAtMs = 0;
  effectiveStartOffsetSecs = 0;
  advanceEpochTrackId = null;
  frozenEndWallClockSuppressionCount = 0;
  suppressWallClockAdvanceActive = false;
}

/** Set when connector emits exclusive-playback-start-failed (suppressWallClockAdvance). */
export function setSuppressWallClockAdvance(active: boolean, reason?: string): void {
  suppressWallClockAdvanceActive = active;
  if (active) {
    frozenEndWallClockSuppressionCount = 0;
  }
  if (typeof console !== 'undefined' && reason) {
    console.log('[PositionReconcile] suppressWallClockAdvance', { active, reason });
  }
}

export function isSuppressWallClockAdvanceActive(): boolean {
  return suppressWallClockAdvanceActive;
}

/** Connector monitor sources that prove natural track completion (not client fallback paths). */
const AUTHORITATIVE_CONNECTOR_TRACK_END_SOURCES = new Set([
  'timer',
  'state',
  'exclusive',
  'exclusive-timer',
]);

export function isAuthoritativeConnectorTrackEndSource(source: string | null | undefined): boolean {
  if (!source) return false;
  return AUTHORITATIVE_CONNECTOR_TRACK_END_SOURCES.has(source);
}

export function isPositionAtGenuineTrackEnd(positionSecs: number, durationSecs: number): boolean {
  if (!isAdvanceGuardDurationTrustworthy(durationSecs)) return false;
  const min = Math.max(0, durationSecs - GENUINE_TRACK_END_TOLERANCE_BEFORE_SECS);
  const max = durationSecs + GENUINE_TRACK_END_TOLERANCE_AFTER_SECS;
  return positionSecs >= min && positionSecs <= max;
}

/** Mid-track stale/runaway device position past the genuine end window — never auto-advance. */
export function isRunawayPositionBeyondTrackEnd(positionSecs: number, durationSecs: number): boolean {
  if (!isAdvanceGuardDurationTrustworthy(durationSecs)) return false;
  return positionSecs > durationSecs + GENUINE_TRACK_END_TOLERANCE_AFTER_SECS;
}

export function getFrozenEndWallClockSuppressionCount(): number {
  return frozenEndWallClockSuppressionCount;
}

export function recordFrozenEndWallClockSuppression(): number {
  frozenEndWallClockSuppressionCount += 1;
  return frozenEndWallClockSuppressionCount;
}

export type PositionPlausibilityInput = {
  reportedPositionSecs: number;
  durationSecs: number;
  sincePlayUriMs: number;
  effectiveStartOffsetSecs: number;
};

export function isDevicePositionPlausible(input: PositionPlausibilityInput): boolean {
  const sincePlayUriSecs = input.sincePlayUriMs / 1000;
  const maxPlausible = input.effectiveStartOffsetSecs + sincePlayUriSecs + POSITION_PLAUSIBILITY_EPSILON_SECS;
  return input.reportedPositionSecs <= maxPlausible;
}

export type DevicePositionDecision = {
  accept: boolean;
  staleLog?: {
    rendererId: string;
    reported: number;
    sincePlayUri: number;
    effective_start_offset: number;
    duration: number;
  };
};

export function evaluateDeviceReportedPosition(params: {
  reportedPositionSecs: number;
  durationSecs: number;
  rendererId: string;
  sincePlayUriMs?: number;
  effectiveStartOffsetSecs?: number;
}): DevicePositionDecision {
  const sincePlayUriMs = params.sincePlayUriMs ?? getSincePlayUriMs();
  const effectiveStartOffset = params.effectiveStartOffsetSecs ?? getEffectiveStartOffsetSecs();
  const plausible = isDevicePositionPlausible({
    reportedPositionSecs: params.reportedPositionSecs,
    durationSecs: params.durationSecs,
    sincePlayUriMs,
    effectiveStartOffsetSecs: effectiveStartOffset,
  });

  if (plausible) {
    return { accept: true };
  }

  return {
    accept: false,
    staleLog: {
      rendererId: params.rendererId,
      reported: params.reportedPositionSecs,
      sincePlayUri: sincePlayUriMs,
      effective_start_offset: effectiveStartOffset,
      duration: params.durationSecs,
    },
  };
}

export type FrozenAtDurationAdvanceInput = {
  durationSecs: number;
  sincePlayUriMs: number;
  effectiveStartOffsetSecs: number;
  positionSecs: number;
  rendererState: string;
};

export function computeWallElapsedSecs(
  sincePlayUriMs: number,
  effectiveStartOffsetSecs: number,
): number {
  return effectiveStartOffsetSecs + sincePlayUriMs / 1000;
}

export function isWallClockAtGenuineTrackEnd(input: FrozenAtDurationAdvanceInput): boolean {
  if (!isAdvanceGuardDurationTrustworthy(input.durationSecs)) return false;
  const wallElapsedSecs = computeWallElapsedSecs(
    input.sincePlayUriMs,
    input.effectiveStartOffsetSecs,
  );
  return wallElapsedSecs >= input.durationSecs - ADVANCE_WALL_CLOCK_GRACE_SECS;
}

export function shouldAllowFrozenAtDurationAdvance(input: FrozenAtDurationAdvanceInput): boolean {
  if (!isAdvanceGuardDurationTrustworthy(input.durationSecs)) return true;
  return isWallClockAtGenuineTrackEnd(input);
}

export type WallClockAdvanceGuardResult = {
  allow: boolean;
  forcedAtFrozenEnd: boolean;
  wallClockEnd: boolean;
  atGenuineEnd: boolean;
  priorSuppressionCount: number;
};

/**
 * Wall-clock advance gate with Pattern 1 yield: at genuine track end, allow at most one
 * suppression; the next encounter forces advance even if wall clock still disagrees.
 */
export function evaluateWallClockAdvanceGuard(
  input: FrozenAtDurationAdvanceInput,
): WallClockAdvanceGuardResult {
  const atGenuineEnd = isPositionAtGenuineTrackEnd(input.positionSecs, input.durationSecs);
  const priorSuppressionCount = frozenEndWallClockSuppressionCount;
  const wallClockEnd = isWallClockAtGenuineTrackEnd(input);
  const stoppedRenderer = input.rendererState === 'STOPPED'
    || input.rendererState === 'NO_MEDIA_PRESENT';

  if (suppressWallClockAdvanceActive) {
    return {
      allow: false,
      forcedAtFrozenEnd: false,
      wallClockEnd: false,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  // Direct advanceTrackEnd / terminal paths without a selection epoch — allow (legacy).
  if (!advanceEpochTrackId && playUriAtMs === 0) {
    return {
      allow: true,
      forcedAtFrozenEnd: false,
      wallClockEnd: false,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  if (!isAdvanceGuardDurationTrustworthy(input.durationSecs)) {
    return {
      allow: true,
      forcedAtFrozenEnd: false,
      wallClockEnd: false,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  // Position-dead renderers: STOPPED + wall clock at track end advances even when
  // device position is stale carryover (e.g. 589s on a 400s track).
  if (
    stoppedRenderer
    && hasActivePlayUriSession()
    && wallClockEnd
  ) {
    return {
      allow: true,
      forcedAtFrozenEnd: false,
      wallClockEnd: true,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  if (
    isRunawayPositionBeyondTrackEnd(input.positionSecs, input.durationSecs)
    && !wallClockEnd
  ) {
    return {
      allow: false,
      forcedAtFrozenEnd: false,
      wallClockEnd: false,
      atGenuineEnd: false,
      priorSuppressionCount,
    };
  }

  if (wallClockEnd) {
    return {
      allow: true,
      forcedAtFrozenEnd: false,
      wallClockEnd: true,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  if (atGenuineEnd && priorSuppressionCount >= 1) {
    return {
      allow: true,
      forcedAtFrozenEnd: true,
      wallClockEnd: false,
      atGenuineEnd,
      priorSuppressionCount,
    };
  }

  return {
    allow: false,
    forcedAtFrozenEnd: false,
    wallClockEnd: false,
    atGenuineEnd,
    priorSuppressionCount,
  };
}

/** Timer / playing→stopped / renderer-track-ended advance — same wall-clock spine as frozen-at-duration. */
export function shouldAllowTimerPathAdvance(
  durationSecs: number,
  sincePlayUriMs?: number,
  effectiveStartOffsetSecs?: number,
  positionSecs?: number,
): boolean {
  // Direct advanceTrackEnd callers (golden tests, legacy) without a selection epoch — allow.
  if (!advanceEpochTrackId && playUriAtMs === 0) {
    return true;
  }
  return evaluateWallClockAdvanceGuard({
    durationSecs,
    sincePlayUriMs: sincePlayUriMs ?? getSincePlayUriMs(),
    effectiveStartOffsetSecs: effectiveStartOffsetSecs ?? getEffectiveStartOffsetSecs(),
    positionSecs: positionSecs ?? 0,
    rendererState: 'STOPPED',
  }).allow;
}

export function capSynthesisPosition(durationSecs: number): number {
  if (durationSecs <= 0) return 0;
  return Math.max(0, durationSecs - 1);
}

export type AdvanceSuppressedLog = {
  rendererId: string;
  position: number;
  duration: number;
  sincePlayUri: number;
  effective_start_offset: number;
  rendererState: string;
  wallElapsedSecs: number;
  path?: string;
  trackId?: string | null;
};

export function buildAdvanceSuppressedWallClockLog(
  rendererId: string,
  positionSecs: number,
  durationSecs: number,
  rendererState: string,
  sincePlayUriMs?: number,
  effectiveStartOffsetSecs?: number,
  extras?: { path?: string; trackId?: string | null },
): AdvanceSuppressedLog {
  const sinceMs = sincePlayUriMs ?? getSincePlayUriMs();
  const offset = effectiveStartOffsetSecs ?? getEffectiveStartOffsetSecs();
  return {
    rendererId,
    position: positionSecs,
    duration: durationSecs,
    sincePlayUri: sinceMs,
    effective_start_offset: offset,
    rendererState,
    wallElapsedSecs: offset + sinceMs / 1000,
    path: extras?.path,
    trackId: extras?.trackId,
  };
}
